import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

const buttonVariants = cva(
  // Base styles
  [
    'inline-flex items-center justify-center gap-2',
    'rounded-button font-medium',
    'transition-all duration-fast ease-out',
    'focus-ring',
    'disabled:opacity-50 disabled:pointer-events-none',
    // Active/pressed state
    'active:scale-[0.98]',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-action text-white',
          'hover:bg-action-hover',
          'active:bg-action-hover',
        ],
        secondary: [
          'bg-transparent border border-border',
          'text-text',
          'hover:bg-surface-variant',
          'active:bg-surface-variant/80',
        ],
        ghost: [
          'bg-transparent',
          'text-text',
          'hover:bg-surface-variant',
          'active:bg-surface-variant/80',
        ],
        destructive: [
          'bg-loss text-white',
          'hover:opacity-90',
          'active:opacity-80',
        ],
      },
      size: {
        sm: 'h-7 px-2.5 text-caption',
        md: 'h-8 px-3 text-label',
        lg: 'h-10 px-4 text-body',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

// Spinner sizes matching button sizes
const spinnerSizes = {
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
  lg: 'h-4 w-4',
};

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Button content */
  children: ReactNode;
  /** Loading state */
  loading?: boolean;
  /** Hide children when loading (show only spinner) */
  loadingText?: string;
}

/**
 * Button component with multiple variants and sizes.
 * Compact sizing for admin data density.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    { children, variant, size = 'md', loading, loadingText, className, disabled, ...props },
    ref
  ) {
    const spinnerSize = spinnerSizes[size || 'md'];

    return (
      <button
        ref={ref}
        className={cn(
          buttonVariants({ variant, size }),
          loading && 'cursor-wait',
          className
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <svg
            className={cn('animate-spin', spinnerSize)}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {loading && loadingText ? loadingText : children}
      </button>
    );
  }
);
